import { useState, useEffect, useCallback, useRef } from 'react'
import req from '@sportninja/common/api/request'

/**
 * Represents a country
 */
export interface Country {
  id: string
  name: string
  name_full: string
  iso_3166_2: string
}

/**
 * Represents a province/state
 */
export interface Province {
  id: string
  name: string
  iso_3166_2: string
}

/**
 * Represents an address
 */
export interface Address {
  id: string
  name: string | null
  name_full: string | null
  street_1: string
  street_2: string | null
  city: string
  postal_code: string
  latitude: number | null
  longitude: number | null
  country?: Country
  province?: Province
}

/**
 * Represents a facility (surface) within a venue
 */
export interface Facility {
  id: string
  name: string
}

/**
 * Represents a venue (location)
 */
export interface Venue {
  id: string
  name: string
  name_full: string
  email: string | null
  website: string | null
  phone: string | null
  timezone: string
  abbreviation: string | null
  has_disabled_access: boolean
  has_bar: boolean
  parking: string
  is_primary: boolean
  address: Address
  facilities: Facility[]
}

/**
 * Hook result interface
 */
interface UseSchedulerLocationsResult {
  /** List of venues selected for the scheduler */
  venues: Venue[]
  /** Loading state */
  loading: boolean
  /** Error state */
  error: Error | null
  /** Function to fetch selected locations */
  fetchLocations: (scheduleId: string) => Promise<void>
  /** Function to refetch current locations */
  refetch: () => Promise<void>
  /** Attach venues to the scheduler selection */
  attachVenues: (venueIds: string[]) => Promise<void>
  /** Detach a venue from the scheduler selection */
  detachVenue: (venueId: string) => Promise<void>
}

/**
 * Custom hook to fetch and manage scheduler-selected venues
 * @param competitionId - Optional competition/schedule ID
 * @returns Object containing venues data, loading state, error state, and CRUD functions
 */
export const useSchedulerLocations = (
  competitionId?: string,
  flightId?: string | null
): UseSchedulerLocationsResult => {
  const [venues, setVenues] = useState<Venue[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const requestVersionRef = useRef(0)

  /**
   * Fetches scheduler-selected venues from the API
   */
  const fetchLocations = useCallback(
    async (scheduleId: string) => {
      if (!scheduleId) {
        setVenues([])
        setLoading(false)
        return
      }

      const version = ++requestVersionRef.current
      setLoading(true)
      setError(null)

      try {
        const qs = flightId ? `?flight_id=${flightId}` : ''
        const { data } = await req(
          `/schedules/${scheduleId}/scheduler/selected-venues${qs}`
        )
        // Discard stale responses from previous requests
        if (version !== requestVersionRef.current) {
          return
        }
        setVenues(data || [])
      } catch (err: unknown) {
        if (version !== requestVersionRef.current) {
          return
        }
        setError(
          err instanceof Error
            ? err
            : new Error('An error occurred while fetching locations')
        )
        setVenues([])
      } finally {
        if (version === requestVersionRef.current) {
          setLoading(false)
        }
      }
    },
    [flightId]
  )

  /**
   * Refetches venues for the current competition
   */
  const refetch = useCallback(async () => {
    if (competitionId) {
      await fetchLocations(competitionId)
    }
  }, [competitionId, fetchLocations])

  /**
   * Attach venues to the scheduler selection
   */
  const attachVenues = useCallback(
    async (venueIds: string[]) => {
      if (!competitionId || venueIds.length === 0) {
        return
      }

      const attachBody: Record<string, unknown> = { venue_ids: venueIds }
      if (flightId) {
        attachBody.flight_id = flightId
      }

      await req(
        `/schedules/${competitionId}/scheduler/selected-venues/attach`,
        {
          method: 'POST',
          body: JSON.stringify(attachBody),
        }
      )
      await fetchLocations(competitionId)
    },
    [competitionId, flightId, fetchLocations]
  )

  /**
   * Detach a venue from the scheduler selection
   */
  const detachVenue = useCallback(
    async (venueId: string) => {
      if (!competitionId) {
        return
      }

      const qs = flightId ? `?flight_id=${flightId}` : ''
      await req(
        `/schedules/${competitionId}/scheduler/selected-venues/${venueId}${qs}`,
        { method: 'DELETE' }
      )
      await fetchLocations(competitionId)
    },
    [competitionId, flightId, fetchLocations]
  )

  // Clear stale data immediately when flightId changes, then refetch
  // Guard: don't fetch when flightId is null (flights haven't loaded yet)
  useEffect(() => {
    setVenues([])
    if (competitionId && flightId) {
      fetchLocations(competitionId)
    }
  }, [competitionId, flightId, fetchLocations])

  return {
    venues,
    loading,
    error,
    fetchLocations,
    refetch,
    attachVenues,
    detachVenue,
  }
}

export default useSchedulerLocations
